import React, { useState, useEffect } from 'react';
import { Scenario } from '../types';

interface ScenarioEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  scenario: Scenario | null;
  onSave: (updatedScenario: Scenario) => void;
}

const ScenarioEditorModal: React.FC<ScenarioEditorModalProps> = ({
  isOpen,
  onClose,
  scenario,
  onSave,
}) => {
  const [editedScenario, setEditedScenario] = useState<Scenario | null>(null);

  useEffect(() => {
    setEditedScenario(scenario);
  }, [scenario]);

  if (!isOpen || !editedScenario) return null;

  const handleSave = () => {
    if (editedScenario) {
      onSave(editedScenario);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-slate-600 flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edycja testu
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 flex-grow overflow-auto">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ID testu</label>
              <input
                type="text"
                value={editedScenario['ID testu']}
                onChange={(e) => setEditedScenario({ ...editedScenario, 'ID testu': e.target.value })}
                className="w-full p-3 bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nazwa testu</label>
              <input
                type="text"
                value={editedScenario['Nazwa testu']}
                onChange={(e) => setEditedScenario({ ...editedScenario, 'Nazwa testu': e.target.value })}
                className="w-full p-3 bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kategoria</label>
              <input
                type="text"
                value={editedScenario['Kategoria']}
                onChange={(e) => setEditedScenario({ ...editedScenario, 'Kategoria': e.target.value })}
                className="w-full p-3 bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kroki testowe</label>
              <textarea
                value={editedScenario['Kroki testowe']}
                onChange={(e) => setEditedScenario({ ...editedScenario, 'Kroki testowe': e.target.value })}
                className="w-full h-40 p-3 bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Oczekiwany rezultat</label>
              <textarea
                value={editedScenario['Oczekiwany rezultat']}
                onChange={(e) => setEditedScenario({ ...editedScenario, 'Oczekiwany rezultat': e.target.value })}
                className="w-full h-32 p-3 bg-gray-50 dark:bg-slate-700 dark:text-gray-100 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-500 transition-all duration-200"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-xl flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Zapisz zmiany
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioEditorModal; 